import React, { useEffect, useState } from 'react';
import { Alert, Form, Input, Message, Modal } from '@bedrock/components';
import { Z_INDEX } from '../../../utils/zIndex';

export interface SendBackModalProps {
  visible: boolean;
  /** 标题，默认「退回单据」（批量审批传「批量退回」） */
  title?: string;
  /** Alert 警示文案；不传时按 count 自动拼：count>0 → 批量文案，否则单笔文案 */
  message?: string;
  /** 批量退回时传已选单据数，自动拼入默认 message */
  count?: number;
  /** 提交中 loading（由调用方在 onConfirm 异步期间控制） */
  loading?: boolean;
  /** 弹框层级：需高于承载它的 Drawer（退款/充值批量抽屉均为 Z_INDEX.drawer=1030），默认 Z_INDEX.modal(1100) */
  zIndex?: number;
  onCancel: () => void;
  /** 退回原因必填校验通过后回调；调用方执行 refundConfirm({action:'REJECT'}) 并管理 loading/关闭 */
  onConfirm: (reason: string) => void;
}

/**
 * 退款退回弹框（单笔/批量共用，FR-12 退回原因必填）
 *
 * 合并 `Drawer/Actions/utils.tsx`（单笔退回）与 `BatchApproveDrawer` 内嵌批量退回
 * 两处结构完全一致的 Alert + Textarea 必填（maxLength 200）弹框：
 * - 单笔：title「退回单据」，Alert「单据将回到申请人的待办中…」
 * - 批量：title「批量退回」，Alert「已勾选 N 张单据将退回申请人的待办中…」（传 count 自动拼）
 * 设计依据：frontend-design.md §5.3 + UX 反馈 2026-08-05（width 680 + 底部按钮自适应 + Alert warning）。
 */
export const SendBackModal: React.FC<SendBackModalProps> = ({
  visible,
  title,
  message,
  count,
  loading,
  zIndex,
  onCancel,
  onConfirm,
}) => {
  const [reason, setReason] = useState('');

  // 关闭时清空原因（对齐原单笔/批量实现 onCancel 内 setReason('')）
  useEffect(() => {
    if (!visible) setReason('');
  }, [visible]);

  const defaultMessage =
    count != null && count > 0
      ? `已勾选 ${count} 张单据将退回申请人的待办中，申请人可修改后重新提交。`
      : '单据将回到申请人的待办中，申请人可修改后重新提交。';

  const handleOk = () => {
    if (!reason.trim()) {
      Message.warn('退回原因必填');
      return;
    }
    onConfirm(reason);
  };

  return (
    <Modal
      title={title ?? '退回单据'}
      width={680}
      visible={visible}
      zIndex={zIndex ?? Z_INDEX.modal}
      onOk={handleOk}
      onCancel={onCancel}
      confirmLoading={loading}
    >
      <div className="flex flex-col gap-3">
        <Alert type="warning" showIcon message={message ?? defaultMessage} />
        <Form layout="vertical">
          <Form.Item
            label="退回原因"
            required
            rules={[{ required: true, message: '请输入退回原因' }]}
          >
            <Input.TextArea
              placeholder="请输入退回原因"
              value={reason}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                setReason(e.target.value)
              }
              rows={3}
              maxLength={200}
              showCount
              block
            />
          </Form.Item>
        </Form>
      </div>
    </Modal>
  );
};

export default SendBackModal;
